import React, { useCallback, useRef, useState } from "react";
import { Download, Printer, FileSpreadsheet, FileDown } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const exportToPrint = (title) => {
  const style = document.createElement("style");
  style.id = "print-override";
  style.textContent = `
    @media print {
      body * { visibility: hidden !important; }
      [data-export-region], [data-export-region] * { visibility: visible !important; }
      [data-export-region] { position: absolute; left: 0; top: 0; width: 100%; }
      nav, aside, [data-no-print] { display: none !important; }
    }
  `;
  document.head.appendChild(style);
  document.title = title || "Export";
  window.print();
  setTimeout(() => { style.remove(); }, 500);
};

const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) { toast.error("No data to export"); return; }
  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(","),
    ...data.map(row => headers.map(h => {
      let val = row[h] ?? "";
      val = String(val).replace(/"/g, '""');
      return `"${val}"`;
    }).join(","))
  ];
  const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${filename}.csv`; a.click();
  URL.revokeObjectURL(url);
  toast.success(`Exported ${data.length} rows to ${filename}.csv`);
};

const exportToExcel = async (data, filename) => {
  if (!data || data.length === 0) { toast.error("No data to export"); return; }
  try {
    const XLSX = await import("xlsx");
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, `${filename}.xlsx`);
    toast.success(`Exported to ${filename}.xlsx`);
  } catch { toast.error("Excel export failed"); }
};

const exportToPDF = async (elementRef, filename, title) => {
  try {
    toast.info("Generating PDF...");
    const html2canvas = (await import("html2canvas")).default;
    const { jsPDF } = await import("jspdf");
    const el = elementRef?.current || document.querySelector("[data-export-region]");
    if (!el) { toast.error("Nothing to export"); return; }
    const canvas = await html2canvas(el, {
      scale: 2, useCORS: true, logging: false,
      backgroundColor: "#ffffff",
      windowWidth: 1200,
    });
    const imgData = canvas.toDataURL("image/jpeg", 0.85);
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const usableW = pageW - margin * 2;

    // Title
    if (title) {
      pdf.setFontSize(14);
      pdf.setFont(undefined, "bold");
      pdf.text(title, margin, 15);
      pdf.setFontSize(8);
      pdf.setFont(undefined, "normal");
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, margin, 20);
    }
    const startY = title ? 25 : margin;
    const usableH = pageH - startY - margin;
    const imgW = usableW;
    const imgH = (canvas.height * imgW) / canvas.width;

    if (imgH <= usableH) {
      pdf.addImage(imgData, "JPEG", margin, startY, imgW, imgH);
    } else {
      // Multi-page
      let y = 0;
      const sliceH = (usableH / imgW) * canvas.width;
      let page = 0;
      while (y < canvas.height) {
        if (page > 0) pdf.addPage();
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        const remaining = canvas.height - y;
        sliceCanvas.height = Math.min(sliceH, remaining);
        const ctx = sliceCanvas.getContext("2d");
        ctx.drawImage(canvas, 0, -y);
        const sliceImg = sliceCanvas.toDataURL("image/jpeg", 0.85);
        const drawH = (sliceCanvas.height * imgW) / sliceCanvas.width;
        pdf.addImage(sliceImg, "JPEG", margin, page === 0 ? startY : margin, imgW, drawH);
        y += sliceH;
        page++;
      }
    }
    pdf.save(`${filename}.pdf`);
    toast.success(`PDF exported: ${filename}.pdf`);
  } catch (e) {
    console.error("PDF export error:", e);
    toast.error("PDF export failed");
  }
};

export const ExportToolbar = ({ title = "Export", csvData, csvFilename, pdfFilename, className = "" }) => {
  const regionRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const handlePDF = useCallback(async () => {
    setExporting(true);
    await exportToPDF(regionRef, pdfFilename || "export", title);
    setExporting(false);
  }, [pdfFilename, title]);

  return (
    <div className={`flex items-center gap-2 ${className}`} data-no-print>
      <Button variant="outline" size="sm" onClick={() => exportToPrint(title)} className="gap-1.5 text-xs border-zinc-300 hover:bg-zinc-100" data-testid="btn-print">
        <Printer size={13} /> Print
      </Button>
      <Button variant="outline" size="sm" onClick={handlePDF} disabled={exporting} className="gap-1.5 text-xs border-zinc-300 hover:bg-zinc-100" data-testid="btn-export-pdf">
        <FileDown size={13} /> {exporting ? "..." : "PDF"}
      </Button>
      {csvData && (
        <>
          <Button variant="outline" size="sm" onClick={() => exportToCSV(csvData, csvFilename || "export")} className="gap-1.5 text-xs border-zinc-300 hover:bg-zinc-100" data-testid="btn-export-csv">
            <Download size={13} /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportToExcel(csvData, csvFilename || "export")} className="gap-1.5 text-xs border-zinc-300 hover:bg-zinc-100" data-testid="btn-export-xlsx">
            <FileSpreadsheet size={13} /> Excel
          </Button>
        </>
      )}
    </div>
  );
};

export { exportToPrint, exportToCSV, exportToExcel, exportToPDF };
export default ExportToolbar;
